import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Card } from '../components/ui/card';
import { Label } from '../components/ui/label';
import { ArrowLeft, Gift, ExternalLink, MapPin, Send } from 'lucide-react';
import api from '../utils/api';
import { toast } from 'sonner';
import useThemeStore from '../store/themeStore';

const DrawResults = () => {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { theme } = useThemeStore();
  
  const [receiver, setReceiver] = useState(null);
  const [wishlist, setWishlist] = useState([]);
  const [address, setAddress] = useState(null);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDrawResult();
  }, [groupId]);

  const fetchDrawResult = async () => {
    try {
      const drawRes = await api.get(`/groups/${groupId}/draw`);
      setReceiver(drawRes.data.receiver);
      
      // Fetch receiver's wishlist
      const wishlistRes = await api.get(`/wishlist/${drawRes.data.receiver.id}`, {
        params: { group_id: groupId },
      });
      setWishlist(wishlistRes.data);
      
      // Try to fetch address
      try {
        const addressRes = await api.get(`/address/${drawRes.data.receiver.id}`, {
          params: { group_id: groupId },
        });
        setAddress(addressRes.data.address);
      } catch (err) {
        // Address not available
      }
    } catch (error) {
      toast.error(t('error'), { description: error.response?.data?.detail || 'Failed to load draw result' });
    }
    setLoading(false);
  };

  const handleSendMessage = async () => {
    if (!message.trim()) {
      toast.error(t('error'), { description: 'Message cannot be empty' });
      return;
    }
    
    try {
      await api.post('/messages', {
        group_id: groupId,
        recipient_id: receiver.id,
        text: message,
      });
      toast.success(t('success'), { description: 'Message sent!' });
      setMessage('');
    } catch (error) {
      toast.error(t('error'), { description: error.response?.data?.detail || 'Failed to send message' });
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-xl">{t('loading')}</div>
      </div>
    );
  }

  if (!receiver) return null;

  return (
    <div className={`min-h-screen ${theme === 'dark' ? 'bg-gray-900' : 'bg-gradient-to-br from-red-50 via-pink-50 to-blue-50'} py-8 px-4`}>
      <div className="container mx-auto max-w-4xl">
        <Button
          data-testid="back-to-group-button"
          variant="ghost"
          onClick={() => navigate(`/group/${groupId}`)}
          className="mb-6"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Group
        </Button>
        
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className="space-y-6"
        >
          {/* Header */}
          <Card className="p-8 text-center bg-gradient-to-br from-rose-100 to-pink-100 dark:from-rose-900 dark:to-pink-900">
            <Gift className="h-16 w-16 text-rose-600 dark:text-rose-300 mx-auto mb-4" />
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
              {t('yourMatch')}
            </h1>
            <p className="text-lg text-gray-700 dark:text-gray-300 mb-4">
              {t('giftingTo')}
            </p>
            <div className="text-4xl font-bold text-rose-600 dark:text-rose-300">
              {receiver.name}
            </div>
          </Card>

          {/* Wishlist */}
          <Card className="p-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              {t('theirWishlist')}
            </h2>
            {wishlist.length === 0 ? (
              <p className="text-center text-gray-500 py-8">{t('noWishlist')}</p>
            ) : (
              <div className="space-y-4">
                {wishlist.map((item) => (
                  <div
                    key={item.id}
                    data-testid={`wishlist-item-${item.id}`}
                    className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4"
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <h3 className="font-semibold text-gray-900 dark:text-white text-lg">
                          {item.title}
                        </h3>
                        {item.description && (
                          <p className="text-gray-600 dark:text-gray-400 mt-2">
                            {item.description}
                          </p>
                        )}
                        <div className="flex gap-4 mt-3">
                          {item.link && (
                            <a
                              href={item.link}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-rose-600 hover:underline flex items-center gap-1"
                            >
                              <ExternalLink className="h-4 w-4" />
                              View Link
                            </a>
                          )}
                          {item.price && (
                            <span className="text-gray-600 dark:text-gray-400 font-medium">
                              ${item.price.toFixed(2)}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Address */}
          <Card className="p-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
              <MapPin className="h-6 w-6" />
              {t('theirAddress')}
            </h2>
            {address ? (
              <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
                <pre className="whitespace-pre-wrap text-gray-900 dark:text-white">
                  {address}
                </pre>
              </div>
            ) : (
              <p className="text-center text-gray-500 py-8">{t('noAddress')}</p>
            )}
          </Card>

          {/* Send Anonymous Message */}
          <Card className="p-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
              {t('sendAnonymousMessage')}
            </h2>
            <div className="space-y-4">
              <div>
                <Label>{t('messageText')}</Label>
                <Textarea
                  data-testid="message-input"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder="Ask a question about their wishlist..."
                  rows={4}
                  className="mt-2"
                />
              </div>
              <Button
                data-testid="send-message-button"
                onClick={handleSendMessage}
                className="w-full"
              >
                <Send className="mr-2 h-4 w-4" />
                {t('sendMessage')}
              </Button>
              <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
                Your identity will remain anonymous
              </p>
            </div>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default DrawResults;